export type ArticleSectionType = "paragraph" | "heading" | "quote";

export interface ArticleSection {
    type: ArticleSectionType;
    content: string;
}

export interface Article {
    id: string;
    category: string;
    title: string;
    excerpt: string;
    image: string;
    date: string;
    readTime: string;
    author: string;
    body: ArticleSection[];
}

export const ARTICLES: Article[] = [
    {
        id: "1",
        category: "Craft",
        title: "The Art of Layering Scents",
        excerpt: "How to build a multi-dimensional olfactive experience in your home using complementary fragrance families.",
        image: "https://images.unsplash.com/photo-1602874801007-bd458bb1b8b6?auto=format&fit=crop&q=80&w=2000",
        date: "12 March 2026",
        readTime: "5 min read",
        author: "Elara Voss",
        body: [
            {
                type: "paragraph",
                content: "A well-scented home is not the result of a single candle burning in a corner. It is an architecture — layered, intentional, and shaped by the way fragrance moves through space.",
            },
            {
                type: "heading",
                content: "Begin with an anchor",
            },
            {
                type: "paragraph",
                content: "Every layered interior fragrance starts with a base note: something earthy, woody, or resinous that grounds the atmosphere. Sandalwood, oud, and cedar are classics for a reason. They are slow to develop, persistent, and generous — they stay in fabric and timber long after the flame is out.",
            },
            {
                type: "quote",
                content: "Scent is not decoration. It is the architecture of a room's mood.",
            },
            {
                type: "heading",
                content: "The fragrance families",
            },
            {
                type: "paragraph",
                content: "To layer effectively, you need to understand the three families home fragrances fall into. Woody and earthy scents anchor — they belong in living rooms and studies. Fresh and citrus scents lift — they are ideal for kitchens, bathrooms, and entrance halls. Florals and spice sit in between, and translate beautifully into bedrooms.",
            },
            {
                type: "heading",
                content: "The rule of two",
            },
            {
                type: "paragraph",
                content: "The most reliable approach is to never burn more than two candles in the same space simultaneously. Let one anchor and one lift. Smoked Oud and Bergamot & Sea Salt is a pairing we return to often — the oud settles low in the room while the bergamot drifts at head height. The effect is not two scents. It is a third thing entirely.",
            },
        ],
    },
    {
        id: "2",
        category: "Seasonal",
        title: "Our Favourite Spring Pairings",
        excerpt: "As the light changes, so should your scent. A guide to transitioning your home fragrance into the new season.",
        image: "https://images.unsplash.com/photo-1455659817273-f96807779a8a?auto=format&fit=crop&q=80&w=2000",
        date: "1 March 2026",
        readTime: "4 min read",
        author: "Maren Holt",
        body: [
            {
                type: "paragraph",
                content: "There is a particular quality to March light. It arrives at a different angle, stays longer, and changes the character of every room it touches. Your home fragrance should change with it.",
            },
            {
                type: "heading",
                content: "What to retire",
            },
            {
                type: "paragraph",
                content: "The heavy, resinous scents that carried you through winter — oud, amber, spiced fig — have done their work. As the temperature lifts and windows open for the first time, they can begin to feel oppressive rather than comforting. Put them away with your wool blankets.",
            },
            {
                type: "quote",
                content: "As the light lengthens, we instinctively reach for brighter, more ephemeral things.",
            },
            {
                type: "heading",
                content: "What to welcome",
            },
            {
                type: "paragraph",
                content: "Spring belongs to Bergamot & Sea Salt. It is a scent that feels like an open window — clean, mineral, faintly coastal. Burn it in the morning, alongside the first coffee. Let it set the register for the day.",
            },
            {
                type: "paragraph",
                content: "For evenings, Velvet Rose makes a graceful transition piece. It has enough warmth to feel appropriate when the nights are still cool, but the rose lifts it unequivocally into the new season. Pair it with fresh tulips on the table. The effect is immediate.",
            },
        ],
    },
    {
        id: "3",
        category: "Care",
        title: "How to Care for Your Candle",
        excerpt: "Trim the wick. Allow a full melt pool. Never burn for more than four hours. The small rituals that make a candle last.",
        image: "https://images.unsplash.com/photo-1605651202774-7d573fd3f12d?auto=format&fit=crop&q=80&w=2000",
        date: "18 February 2026",
        readTime: "3 min read",
        author: "Elara Voss",
        body: [
            {
                type: "paragraph",
                content: "A well-made candle is an investment. The wax, the fragrance, the wick, the vessel — each has been chosen with care. The way you burn it determines whether you honour that care or waste it.",
            },
            {
                type: "heading",
                content: "The first burn",
            },
            {
                type: "paragraph",
                content: "The first time you light a candle, allow it to burn until the entire surface has melted — edge to edge. This is called a full melt pool, and it is the single most important thing you can do. Extinguishing early causes the wax to tunnel down the centre, wasting the fragrance locked in the outer walls.",
            },
            {
                type: "quote",
                content: "Patience is the first principle of candle care.",
            },
            {
                type: "heading",
                content: "The wick",
            },
            {
                type: "paragraph",
                content: "Before every burn, trim your wick to 5mm. A wick that is too long creates a large, unsteady flame that deposits soot on the glass and burns through the wax too quickly. Trim it cold, before lighting. A wick trimmer is ideal — scissors work if you remove the trimmings from the vessel before lighting.",
            },
            {
                type: "heading",
                content: "Extinguishing",
            },
            {
                type: "paragraph",
                content: "Blow out a candle and you disturb everything — the wick is knocked sideways, the wax ripples, and a plume of acrid smoke drifts through the room. Use a snuffer instead, or dip the lit wick into the wax pool and lift it back to centre. It extinguishes cleanly and primes the wick for the next burn.",
            },
        ],
    },
    {
        id: "4",
        category: "Behind the Collection",
        title: "The Making of Smoked Oud",
        excerpt: "A journey to the oud forests of Laos, and how a single raw material became our most complex fragrance.",
        image: "https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format&fit=crop&q=80&w=2000",
        date: "5 February 2026",
        readTime: "6 min read",
        author: "Maren Holt",
        body: [
            {
                type: "paragraph",
                content: "Smoked Oud took longer to make than any other candle in the collection. Two years from first concept to final pour. That is not unusual for oud — it is not a material that yields quickly to anyone.",
            },
            {
                type: "heading",
                content: "The rarest material in perfumery",
            },
            {
                type: "paragraph",
                content: "Oud is produced when the Aquilaria tree responds to fungal infection by secreting a dense, fragrant resin deep within its heartwood. The process takes decades. The resulting material — dark, complex, and unlike anything else — has been traded along the Silk Road for over a thousand years. Only a small fraction of Aquilaria trees ever produce it.",
            },
            {
                type: "quote",
                content: "We spent two years getting the oud right. It is not the kind of material you rush.",
            },
            {
                type: "heading",
                content: "The journey to Laos",
            },
            {
                type: "paragraph",
                content: "Our perfumer, Caspar Eld, first encountered the raw material at a small family-run distillery in rural Laos, two hours from the nearest town. The operation was modest — a handful of stills, a wood-fired furnace, and rows of heartwood waiting to be processed. The oud produced there was unlike the synthetic approximations common in commercial fragrance: it was raw, animal, and deeply resinous.",
            },
            {
                type: "heading",
                content: "Fourteen iterations",
            },
            {
                type: "paragraph",
                content: "Getting the balance right took fourteen iterations across eight months. The challenge was not the oud itself, but what to put around it. Too much cedar and the resin is buried. Too much pepper and the smoke becomes aggressive. The version we settled on — our fourteenth — opens with a clean cedar bite, the pepper arrives within minutes, and then the oud emerges slowly over the course of an hour. It is, we think, exactly right.",
            },
        ],
    },
];
